import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { MoreHoriz, AttachFile, } from "@mui/icons-material";

import { isOnlyEmojis } from "../../utils/utils";
import AuthImage from "./AuthImage";

// Bytes -> "23 B", "4.2 KB", "1.3 MB", "2.10 GB".
// Returns '' when the size is unknown (missing / 0 / not a number), so no fake "0.0 KB" is shown.
export const formatSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";

  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// "New" tag on an attachment the receiver has not opened yet
export const NewBadge = ({ sx }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: "1px",
      borderRadius: "999px",
      fontSize: "0.62rem",
      fontWeight: 800,
      letterSpacing: "0.06em",
      lineHeight: 1.5,
      textTransform: "uppercase",
      color: (theme) => theme.palette.getContrastText(theme.palette.secondary.main),
      backgroundColor: (theme) => theme.palette.secondary.main,
      flexShrink: 0,
      ...sx,
    }}
  >
    New
  </Box>
);

const MessageContent = ({
  message,
  handleOpenAttachmentMenu,
  onOpenImage,
  onOpenFile,
  isNew = false,
  isMine = false,
}) => {

  const isEmojiOnly = message.content && isOnlyEmojis(message.content);

  const attachmentUrl = message.attachment ? `${process.env.REACT_APP_SERVER_URL}${message.attachment.url}` : null;

  const sizeText = formatSize(message.attachment?.size);

  return (
    <>
      {message.attachment && (
        <Box
          mb={message.content ? 1 : 0}
          sx={{
            display:"flex",
            flexDirection:"column",
            gap:1,
            maxWidth:300,
          }}
        >
          {message.attachment.type?.startsWith("image/") ? (
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                maxWidth: "100%",
              }}
            >
              <AuthImage
                src={attachmentUrl}
                alt={message.attachment.name}
                sx={{
                  maxWidth:"100%",
                  maxHeight:250,
                  borderRadius:2,
                  objectFit:"contain",
                  cursor:"pointer",
                  display:"block",
                }}
                onClick={() => onOpenImage?.(message)}
              />

              {isNew && <NewBadge sx={{ position: "absolute", top: 6, left: 6, boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />}

              <IconButton
                size="small"
                onClick={(e)=>{
                  e.stopPropagation();
                  handleOpenAttachmentMenu(e,message);
                }}
                sx={{
                  position:"absolute",
                  top:4,
                  right:4,
                  backgroundColor:"rgba(0,0,0,0.45)",
                  color:"#fff",

                  opacity:0,

                  transition:"opacity .2s",

                  "&:hover":{
                    backgroundColor:"rgba(0,0,0,0.65)",
                  },

                  // always show on hover
                  ".MuiBox-root:hover &":{
                    opacity:1,
                  }
                }}
              >
                <MoreHoriz fontSize="small"/>
              </IconButton>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              p={1}
              borderRadius={1}
              onClick={() => onOpenFile?.(message)}
              title="Open (or download if it can't be opened in the browser)"
              sx={{
                // a light tint on both my gold bubble and theirs (white text on both)
                backgroundColor: isMine ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.08)",
                cursor: onOpenFile ? "pointer" : "default",
                "&:hover": onOpenFile
                  ? { backgroundColor: isMine ? "rgba(255,255,255,0.24)" : "rgba(255,255,255,0.14)" }
                  : undefined,
              }}
            >
              <AttachFile fontSize="small"/>
              <Box flex={1}>
                <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {message.attachment.name}
                  {isNew && <NewBadge />}
                </Typography>

                {sizeText && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "inherit", opacity: 0.72 }}
                  >
                    {sizeText}
                  </Typography>
                )}
              </Box>

              <IconButton
                size="small"
                color="inherit"
                onClick={(e)=>{
                  e.stopPropagation();
                  handleOpenAttachmentMenu(e,message);
                }}
              >
                <MoreHoriz fontSize="small"/>
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {message.content && (
        <Typography
          variant={isEmojiOnly ? "h3":"body2"}
          sx={{
            whiteSpace:"pre-wrap",
            color:"inherit",
            fontSize:
              isEmojiOnly
                ? "3.5rem"
                :"inherit",
            lineHeight:
              isEmojiOnly
                ? 1.1
                :"inherit",
          }}
        >
          {message.content}
        </Typography>
      )}
    </>
  );
};

export default React.memo(MessageContent);